import React, { useState, useEffect } from 'react';
import AuthUtils from '../auth/auth_utils';
import { profileData } from '../data/data_profile';
import { userData } from '../data/data_user';
import { authData } from '../data/data_auth';
import { useTheme } from '@mui/material/styles';
import ProfileSkeletonLoader from './profile_skeleton';
import { 
    Box, 
    Paper, 
    Typography, 
    Grid, 
    Avatar, 
    TextField, 
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    Tooltip
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getAuthProviderUrl } from '../utils/auth_url';
import { triggerProfileUpdate } from '../utils/profile_events';

function ProfileLoader() {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState(null);
    const [editing, setEditing] = useState({
        firstName: false,
        lastName: false,
        imageUrl: false
    });
    const [editedValues, setEditedValues] = useState({
        firstName: '',
        lastName: '',
        imageUrl: ''
    });
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const theme = useTheme();
    const modeClass = theme.palette.mode === 'dark' ? 'dark' : 'light';
    const tokenUser = AuthUtils.ExtractTokenUser();

    const isValidImageUrl = (url) => {
        if (!url) return false;
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const profileResponse = await profileData.getProfile();
                setData(profileResponse);
                
                // Get the image URL from the response
                let imageUrl = '';
                if (profileResponse.image_url && isValidImageUrl(profileResponse.image_url)) {
                    imageUrl = profileResponse.image_url;
                }
                
                setEditedValues({
                    firstName: profileResponse.first_name || '',
                    lastName: profileResponse.last_name || '',
                    imageUrl: imageUrl
                });
            } catch (error) {
                console.error('Error loading profile:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const handleEdit = (field) => {
        setEditing(prev => ({ ...prev, [field]: true }));
    };

    const handleBlur = async (field) => {
        const newValue = editedValues[field];
        // If the field is empty, revert to previous value
        if (!newValue) {
            setEditedValues(prev => ({
                ...prev,
                [field]: data[field === 'firstName' ? 'first_name' : field === 'lastName' ? 'last_name' : 'image_url'] || ''
            }));
        } else {
            // Save the change
            try {
                const updatedData = {
                    first_name: field === 'firstName' ? newValue : editedValues.firstName,
                    last_name: field === 'lastName' ? newValue : editedValues.lastName,
                    image_url: field === 'imageUrl' ? newValue : editedValues.imageUrl,
                    email: data.email
                };
                await profileData.updateProfile(updatedData);
                setData(prev => ({ ...prev, ...updatedData }));
                setSnackbar({
                    open: true,
                    message: 'Profile updated successfully',
                    severity: 'success'
                });
                
                // Trigger profile update event if image URL was changed
                if (field === 'imageUrl') {
                    triggerProfileUpdate();
                }
            } catch (error) {
                console.error('Error updating profile:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to update profile',
                    severity: 'error'
                });
                // Revert to previous value on error
                setEditedValues(prev => ({
                    ...prev,
                    [field]: data[field === 'firstName' ? 'first_name' : field === 'lastName' ? 'last_name' : 'image_url'] || ''
                }));
            }
        }
        setEditing(prev => ({ ...prev, [field]: false }));
    };

    const handlePasswordChange = async () => {
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setSnackbar({
                open: true,
                message: 'New passwords do not match',
                severity: 'error'
            });
            return;
        }

        try {
            await authData.changePassword({
                password: passwordData.currentPassword,
                new_password: passwordData.newPassword
            });
            
            setShowPasswordDialog(false);
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            setSnackbar({
                open: true,
                message: 'Password changed successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error changing password:', error);
            setSnackbar({
                open: true,
                message: 'Failed to change password',
                severity: 'error'
            });
        }
    };

    const showCurrentPasswordCheck = passwordData.currentPassword.length > 0;
    const showNewPasswordCheck = passwordData.newPassword.length > 0;
    const showConfirmPasswordCheck = passwordData.newPassword === passwordData.confirmPassword && passwordData.confirmPassword.length > 0;

    if (isLoading) {
        return <ProfileSkeletonLoader modeClass={modeClass} />;
    }

    return (
        <Box sx={{ maxWidth: '800px', margin: '40px auto', padding: '0 20px' }}>
            <Paper elevation={3} sx={{ padding: '30px' }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item>
                        <Avatar 
                            sx={{ 
                                width: 80, 
                                height: 80, 
                                bgcolor: theme.palette.primary.main,
                                '& img': {
                                    objectFit: 'cover'
                                }
                            }}
                            src={isValidImageUrl(editedValues.imageUrl) ? editedValues.imageUrl : undefined}
                            imgProps={{
                                onError: (e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.style.backgroundColor = theme.palette.primary.main;
                                }
                            }}
                        >
                            <AccountCircleIcon sx={{ fontSize: 60 }} />
                        </Avatar>
                    </Grid>
                    <Grid item xs>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
                                    First Name
                                </Typography>
                                {editing.firstName ? (
                                    <TextField
                                        fullWidth
                                        value={editedValues.firstName}
                                        onChange={(e) => setEditedValues(prev => ({ ...prev, firstName: e.target.value }))}
                                        onBlur={() => handleBlur('firstName')}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.target.blur();
                                            }
                                        }}
                                        autoFocus
                                    />
                                ) : (
                                    <Typography 
                                        variant="h6" 
                                        onClick={() => handleEdit('firstName')}
                                        sx={{ 
                                            cursor: 'pointer',
                                            color: editedValues.firstName ? 'inherit' : 'text.secondary'
                                        }}
                                    >
                                        {editedValues.firstName || 'First Name'}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
                                    Last Name
                                </Typography>
                                {editing.lastName ? (
                                    <TextField
                                        fullWidth
                                        value={editedValues.lastName}
                                        onChange={(e) => setEditedValues(prev => ({ ...prev, lastName: e.target.value }))}
                                        onBlur={() => handleBlur('lastName')}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.target.blur();
                                            }
                                        }}
                                    />
                                ) : (
                                    <Typography 
                                        variant="h6" 
                                        onClick={() => handleEdit('lastName')}
                                        sx={{ 
                                            cursor: 'pointer',
                                            color: editedValues.lastName ? 'inherit' : 'text.secondary'
                                        }}
                                    >
                                        {editedValues.lastName || 'Last Name'}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 2, mb: 1 }}>
                            Email
                        </Typography>
                        <Typography variant="body1" color="textSecondary">
                            {data?.email || tokenUser || 'No email available'}
                        </Typography>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 4 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary" sx={{ mb: 1 }}>
                                Image URL
                            </Typography>
                            {editing.imageUrl ? (
                                <TextField
                                    fullWidth
                                    value={editedValues.imageUrl}
                                    onChange={(e) => setEditedValues(prev => ({ ...prev, imageUrl: e.target.value }))}
                                    onBlur={() => handleBlur('imageUrl')}
                                    placeholder="https://example.com/avatar.jpg"
                                />
                            ) : (
                                <Tooltip title={editedValues.imageUrl || 'https://example.com/avatar.jpg'}>
                                    <Paper 
                                        elevation={1} 
                                        sx={{ 
                                            padding: '15px',
                                            bgcolor: theme.palette.background.default,
                                            cursor: 'pointer',
                                            '& .truncated-url': {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }
                                        }}
                                        onClick={() => handleEdit('imageUrl')}
                                    >
                                        <Typography 
                                            variant="body1" 
                                            color={editedValues.imageUrl ? 'inherit' : 'text.secondary'}
                                            className="truncated-url"
                                        >
                                            {editedValues.imageUrl || 'https://example.com/avatar.jpg'}
                                        </Typography>
                                    </Paper>
                                </Tooltip>
                            )}
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ mt: 4 }}>
                    <Button 
                        variant="outlined" 
                        onClick={() => setShowPasswordDialog(true)}
                    >
                        Change Password
                    </Button>
                </Box>
            </Paper>

            <Dialog 
                open={showPasswordDialog} 
                onClose={() => setShowPasswordDialog(false)}
                PaperProps={{
                    sx: { width: '450px' }
                }}
            >
                <DialogTitle>Change Password</DialogTitle>
                <Typography 
                    variant="body2" 
                    sx={{ 
                        px: 3, 
                        color: 'text.secondary',
                        fontStyle: 'italic'
                    }}
                >
                    Password must contain: lowercase letter, uppercase letter, number, and special character
                </Typography>
                <DialogContent>
                    <Box sx={{ 
                        pt: 2, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: 2,
                        width: '70%',
                        margin: '0 auto'
                    }}>
                        <TextField
                            label="Current Password"
                            type="password"
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                            InputProps={{
                                endAdornment: showCurrentPasswordCheck && (
                                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                                )
                            }}
                        />
                        <TextField
                            label="New Password"
                            type="password"
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                            InputProps={{
                                endAdornment: showNewPasswordCheck && (
                                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                                )
                            }}
                        />
                        <TextField
                            label="Confirm New Password"
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            InputProps={{
                                endAdornment: showConfirmPasswordCheck && (
                                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                                )
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
                    <Button 
                        onClick={handlePasswordChange} 
                        variant="contained"
                        sx={{ 
                            mt: 1,
                            mr: 1,
                            mb: 1
                        }}
                    >
                        Change Password
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            >
                <Alert 
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ProfileLoader;

